import React, { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';
import CloseSVG from '@birdi/icons/close.svg';
import InfoSVG from '@birdi/icons/info.svg';
import {
  DISMISS_STORAGE_KEY,
  MAINTENANCE_WINDOW,
  isWindowUpcoming,
  localWindowLabel,
  todayKey,
} from './window';

const readDismissedOn = (): string | null => {
  try {
    return window.localStorage.getItem(DISMISS_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeDismissedOn = (day: string) => {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, day);
  } catch {
    // Private mode or storage disabled: banner simply stays dismissable per session.
  }
};

/**
 * Site-wide notice for a scheduled maintenance window. Styled to match
 * TrialBanner. Dismissal is stored per local calendar day, so a dismissed
 * banner comes back at the user's local midnight until the window has passed.
 */
export const MaintenanceBanner: React.FC = () => {
  const [today, setToday] = useState(todayKey);
  const [dismissedOn, setDismissedOn] = useState(readDismissedOn);

  // Re-evaluate once a minute so a tab left open past midnight re-shows the
  // banner, and so it disappears on its own once the window has closed.
  useEffect(() => {
    const tick = () => setToday(todayKey());
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, []);

  if (!isWindowUpcoming() || dismissedOn === today) {
    return null;
  }

  const dismiss = () => {
    writeDismissedOn(today);
    setDismissedOn(today);
  };

  const local = localWindowLabel();

  return (
    <div
      role="status"
      data-testid="maintenance-banner"
      css={(theme: BirdiTheme) => css`
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${theme.lightOcean};
        padding: 0.875rem 3rem 0.875rem 1.5rem;
        box-sizing: border-box;
        width: 100%;
        flex-shrink: 0;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 0.75rem;
        `}
      >
        <div
          css={(theme: BirdiTheme) => css`
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
              width: 20px;
              height: 20px;
              fill: none;
              stroke: ${theme.midnight};
              stroke-width: 2;
            }
          `}
        >
          <InfoSVG />
        </div>
        <div
          css={(theme: BirdiTheme) => css`
            color: ${theme.midnight};
            font-size: 1.1em;
            font-weight: 400;
            line-height: 1.5;
          `}
        >
          <strong>Scheduled maintenance: {MAINTENANCE_WINDOW.label}</strong>
          {local && <> ({local} your time)</>}
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss banner"
        css={css`
          position: absolute;
          right: 1.5rem;
          flex-shrink: 0;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: background 0.2s;
          &:hover {
            background: rgba(0, 0, 0, 0.05);
          }
        `}
      >
        <div
          css={(theme: BirdiTheme) => css`
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
              width: 16px;
              height: 16px;
              fill: ${theme.midnight};
            }
          `}
        >
          <CloseSVG />
        </div>
      </button>
    </div>
  );
};
